import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from '../utils/axios';
import { isAuth } from '../utils/auth';
import './GenerateQR.css';

const DEFAULT_URL = 'https://tramitespronto.com.ar';
const DEFAULT_CAPTION = 'Visitá nuestra web';

const GenerateQR = () => {
    const [url, setUrl] = useState(DEFAULT_URL);
    const [caption, setCaption] = useState(DEFAULT_CAPTION);
    const [qrUrl, setQrUrl] = useState('');
    const [qrCaption, setQrCaption] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const qrRef = useRef(null);
    const svgRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchQr = async () => {
            const auth = await isAuth();
            if (!auth) {
                navigate('/login');
                return;
            }
            try {
                const response = await axios.get('/qr');
                if (response.data.hasQr) {
                    setUrl(response.data.url);
                    setQrUrl(response.data.url);
                    setCaption(response.data.caption || '');
                    setQrCaption(response.data.caption || '');
                }
            } catch (error) {
                console.error('Error al cargar QR guardado:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchQr();
    }, [navigate]);

    const handleGenerate = async (e) => {
        e.preventDefault();
        const trimmedUrl = url.trim();
        const trimmedCaption = caption.trim();
        if (!trimmedUrl) return;

        setSaving(true);
        try {
            await axios.post('/qr', { url: trimmedUrl, caption: trimmedCaption });
            setQrUrl(trimmedUrl);
            setQrCaption(trimmedCaption);
            toast.success('QR generado y guardado');
        } catch (error) {
            console.error('Error al guardar QR:', error);
            toast.error('No se pudo guardar el QR');
        } finally {
            setSaving(false);
        }
    };

    const handleDownload = () => {
        const qrCanvas = qrRef.current?.querySelector('canvas');
        if (!qrCanvas) return;

        const qrSize = qrCanvas.width;
        const padding = 48;
        const captionHeight = qrCaption ? 110 : 0;

        const frame = document.createElement('canvas');
        frame.width = qrSize + padding * 2;
        frame.height = qrSize + padding * 2 + captionHeight;
        const ctx = frame.getContext('2d');

        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, frame.width, frame.height);
        ctx.drawImage(qrCanvas, padding, padding);

        if (qrCaption) {
            ctx.fillStyle = '#fff';
            ctx.font = 'bold 44px Arial, sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(qrCaption, frame.width / 2, qrSize + padding + captionHeight / 2, frame.width - padding);
        }

        const link = document.createElement('a');
        link.download = 'qr-tramites-pronto.png';
        link.href = frame.toDataURL('image/png');
        link.click();
    };

    const escapeXml = (text) =>
        text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const handleDownloadSvg = () => {
        const svgEl = svgRef.current?.querySelector('svg');
        if (!svgEl) return;

        const qrSize = 512;
        const padding = 48;
        const captionHeight = qrCaption ? 110 : 0;
        const width = qrSize + padding * 2;
        const height = qrSize + padding * 2 + captionHeight;

        const inner = svgEl.cloneNode(true);
        inner.setAttribute('x', padding);
        inner.setAttribute('y', padding);
        inner.setAttribute('width', qrSize);
        inner.setAttribute('height', qrSize);

        const captionText = qrCaption
            ? `<text x="${width / 2}" y="${qrSize + padding + captionHeight / 2}" fill="#fff" font-family="Arial, sans-serif" font-size="44" font-weight="bold" text-anchor="middle" dominant-baseline="middle">${escapeXml(qrCaption)}</text>`
            : '';

        const svgString =
            `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">` +
            `<rect width="${width}" height="${height}" fill="#000"/>` +
            new XMLSerializer().serializeToString(inner) +
            captionText +
            `</svg>`;

        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const link = document.createElement('a');
        link.download = 'qr-tramites-pronto.svg';
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
    };

    return (
        <div className="qr-page">
            <ToastContainer />
            <div className="qr-card">
                <h1 className="qr-title">
                    <i className="fa-solid fa-qrcode"></i>
                    Código QR de la web
                </h1>
                <p className="qr-subtitle">
                    Generá un código QR que lleva directo a la página. Podés imprimirlo o compartirlo donde quieras.
                </p>

                <form className="qr-form" onSubmit={handleGenerate}>
                    <label className="qr-label">
                        URL de destino
                        <input
                            type="url"
                            className="qr-input"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder={DEFAULT_URL}
                            required
                        />
                    </label>
                    <label className="qr-label">
                        Texto debajo del QR
                        <input
                            type="text"
                            className="qr-input"
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            placeholder={DEFAULT_CAPTION}
                            maxLength={40}
                        />
                    </label>
                    <button type="submit" className="qr-btn qr-btn-primary" disabled={saving}>
                        <i className="fa-solid fa-qrcode"></i>
                        {saving ? 'GUARDANDO...' : 'GENERAR QR'}
                    </button>
                </form>

                {loading && (
                    <p className="qr-loading">Cargando QR guardado...</p>
                )}

                {!loading && qrUrl && (
                    <div className="qr-result">
                        <div className="qr-frame" ref={qrRef}>
                            <QRCodeCanvas
                                value={qrUrl}
                                size={512}
                                level="H"
                                marginSize={2}
                            />
                            {qrCaption && (
                                <span className="qr-caption">{qrCaption}</span>
                            )}
                        </div>
                        <span className="qr-url-label">{qrUrl}</span>
                        <div className="qr-download-buttons">
                            <button type="button" className="qr-btn qr-btn-secondary" onClick={handleDownload}>
                                <i className="fa-solid fa-download"></i>
                                Descargar PNG
                            </button>
                            <button type="button" className="qr-btn qr-btn-secondary" onClick={handleDownloadSvg}>
                                <i className="fa-solid fa-vector-square"></i>
                                Descargar SVG
                            </button>
                        </div>
                        <div ref={svgRef} style={{ display: 'none' }}>
                            <QRCodeSVG
                                value={qrUrl}
                                size={512}
                                level="H"
                                marginSize={2}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GenerateQR;
